import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];
const ITERATION_STATUS_OPTIONS = ["", "Approved", "Rejected"];

const COLUMNS = [
  { key: "id",                    label: "ID",                   editable: false,                width: 80  },
  { key: "parent_sku",            label: "Parent SKU",           editable: false,                width: 150 },
  { key: "images_link",           label: "Images Link",          editable: true,                 width: 240 },
  { key: "catalogue_link",        label: "Catalogue Link",       editable: true,                 width: 240 },
  { key: "video_link",            label: "Video Link",           editable: true,                 width: 240 },
  { key: "packaging_link",        label: "Packaging Link",       editable: false,                width: 240 },
  { key: "packaging_iterations",  label: "Packaging Iterations", editable: false, isIterations: true, width: 190 },
  { key: "status",                label: "Status",               editable: true,  badge: true,   width: 120 },
  { key: "status_updated_date",   label: "Status Date",          editable: true,                 width: 130 },
  { key: "target_date",           label: "Target Date",          editable: true,                 width: 130 },
  { key: "remarks",               label: "Remarks",              editable: true,                 width: 220 },
  { key: "updated_at",            label: "Updated At",           editable: false,                width: 180 },
];

const DEFAULT_VISIBLE = ["id", "parent_sku", "catalogue_link", "packaging_link", "packaging_iterations", "status", "status_updated_date", "target_date", "updated_at"];
const DEFAULT_FILTERS = { search: "", parent_sku: "", status: "" };

const STATUS_FILTER_FALLBACK = [
  { v: "draft",    l: "Draft"    },
  { v: "approved", l: "Approved" },
  { v: "live",     l: "Live"     },
  { v: "rejected", l: "Rejected" },
];

const IMPORTABLE_FIELDS = new Set(COLUMNS.map(c => c.key));

const SKU_POPUP_FIELDS = [
  "id", "business_type", "parent_sku", "sku", "sku_name", "sku_reference_code",
  "sku_type", "ean_upc", "hsn_code", "tax_rate", "mrp", "components",
  "category", "sub_category", "color", "pot", "sku_size", "sku_actual_dimensions",
  "sku_actual_weight_in_kg", "combo_of", "sku_cost", "sku_cost_tax",
  "sku_inner_packaging_w_tax", "sku_outer_packaging_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "sku_status", "sku_tag", "cost_audit", "attr_audit", "created_at", "updated_at",
];

// ─── Iteration helpers ────────────────────────────────────────────────────────

function normalizeIterations(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try { const p = JSON.parse(data); return Array.isArray(p) ? p : []; }
    catch { return []; }
  }
  return [];
}

function createIterationDraft(no) {
  return { iteration_no: no, date: "", link: "", status: "", status_update_date: "", approved_by: "" };
}

function getApprovedPackagingLink(iterations) {
  const approved = normalizeIterations(iterations).find(
    item => String(item?.status || "").trim().toLowerCase() === "approved"
  );
  return approved?.link || null;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch { return {}; }
}

function parseListResponse(data) {
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data))    return data.data;
  if (Array.isArray(data))          return data;
  return [];
}

async function fetchRows({ page, pageSize, filters }) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (filters.search)     params.set("search",     filters.search);
  if (filters.parent_sku) params.set("parent_sku", filters.parent_sku);
  if (filters.status)     params.set("status",     filters.status);
  const response = await fetch(`${BASE_URL}/catalogue/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch catalogue");
  return data;
}

async function fetchDropdownOptions(fieldName) {
  const params = new URLSearchParams({ field_name: fieldName });
  const response = await fetch(`${BASE_URL}/dropdowns/options/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Failed to fetch ${fieldName} options`);
  return parseListResponse(data)
    .map(row => ({ v: String(row?.value ?? "").trim(), l: String(row?.label ?? row?.value ?? "").trim() }))
    .filter(row => row.v);
}

async function fetchSkusByParentSku(parentSku) {
  const params = new URLSearchParams({ search: parentSku, page_size: "500" });
  const response = await fetch(`${BASE_URL}/sku/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch SKU rows");
  return parseListResponse(data).filter(
    r => String(r?.parent_sku || "").trim() === String(parentSku || "").trim()
  );
}

async function createCatalogue(payload) {
  const response = await fetch(`${BASE_URL}/catalogue/create/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Create failed");
  return data?.data || data;
}

async function updateCatalogue(id, payload) {
  const response = await fetch(`${BASE_URL}/catalogue/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Update failed for id ${id}`);
  return data?.data || data;
}

async function pseudoBulkUpdate(rows) {
  let updated = 0, failed = 0;
  for (const row of rows) {
    try {
      const payload = { ...row };
      delete payload.id;
      await updateCatalogue(row.id, payload);
      updated++;
    } catch { failed++; }
  }
  return { updated, failed };
}

// ─── CSV utilities ────────────────────────────────────────────────────────────

function csvSafe(v) {
  const s = String(v ?? "");
  return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows) {
  const fields = Array.from(IMPORTABLE_FIELDS);
  return `${fields.join(",")}\n${rows.map(r => fields.map(f => csvSafe(r[f])).join(",")).join("\n")}`;
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const rows = []; let cur = "", line = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nx = text[i + 1];
    if (ch === '"' && inQ && nx === '"') { cur += '"'; i++; continue; }
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { line.push(cur); cur = ""; continue; }
    if ((ch === "\n" || ch === "\r") && !inQ) {
      if (ch === "\r" && nx === "\n") i++;
      line.push(cur); rows.push(line); line = []; cur = ""; continue;
    }
    cur += ch;
  }
  if (cur.length > 0 || line.length > 0) { line.push(cur); rows.push(line); }
  return rows;
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// ─── Shared style ─────────────────────────────────────────────────────────────

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = memo(({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const wrapperCls = {
    success: "border-green-500 bg-green-50 text-green-800",
    error:   "border-red-500 bg-red-50 text-red-800",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-800",
    info:    "border-blue-500 bg-blue-50 text-blue-800",
  };
  const dotCls = { success: "bg-green-500", error: "bg-red-500", warning: "bg-yellow-500", info: "bg-blue-500" };
  return (
    <div className={cn(
      "fixed bottom-6 right-6 z-[9999] flex items-center gap-3 max-w-sm",
      "rounded-lg border-2 px-4 py-3 shadow-xl text-sm",
      wrapperCls[type] || wrapperCls.info
    )}>
      <span className={cn("w-2 h-2 rounded-full shrink-0", dotCls[type] || dotCls.info)} />
      <span className="flex-1">{message}</span>
      <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
});

// ─── EditableCell ─────────────────────────────────────────────────────────────

const EditableCell = memo(({ value, rowId, colKey, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(String(value ?? ""));

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== String(value ?? "")) onChange(rowId, colKey, draft);
  }, [draft, value, rowId, colKey, onChange]);

  if (editing) return (
    <Input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter")  commit();
        if (e.key === "Escape") { setDraft(String(value ?? "")); setEditing(false); }
      }}
      className="h-7 text-xs py-0 px-1.5"
    />
  );

  return (
    <span
      onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
      title="Click to edit"
      className={cn(
        "block w-full cursor-text truncate px-0.5 py-0.5 rounded text-sm transition-colors hover:bg-accent",
        value ? "text-foreground" : "text-muted-foreground"
      )}
    >
      {String(value ?? "—")}
    </span>
  );
});

// ─── SortIcon ─────────────────────────────────────────────────────────────────

function SortIcon({ dir }) {
  if (dir === "asc")  return <ArrowUp   className="w-3 h-3 text-primary shrink-0" />;
  if (dir === "desc") return <ArrowDown className="w-3 h-3 text-primary shrink-0" />;
  return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40 shrink-0" />;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  live:     "bg-green-100 text-green-700 border-green-200",
  approved: "bg-blue-100 text-blue-700 border-blue-200",
  draft:    "bg-yellow-100 text-yellow-700 border-yellow-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const label = String(value).replace(/_/g, " ");
  const style = STATUS_STYLES[String(value).toLowerCase()];
  if (!style) return <span className="text-muted-foreground text-xs capitalize">{label}</span>;
  return <Badge className={cn("capitalize text-xs font-medium", style)}>{label}</Badge>;
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────

const ResizeHandle = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors"
  />
);

// ─── ColumnPicker ─────────────────────────────────────────────────────────────

const ColumnPicker = memo(({ all, visible, onToggle, onSelectAll, onReset }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-muted-foreground">
        Visible: <strong className="text-foreground">{visible.length}</strong> / {all.length}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onSelectAll}>All</Button>
        <Button variant="outline" size="sm" onClick={onReset}>Reset</Button>
      </div>
    </div>
    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
      {all.map(col => {
        const on = visible.includes(col.key);
        return (
          <label
            key={col.key}
            className={cn(
              "flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded-lg text-sm border transition-all",
              on ? "bg-violet-100 text-violet-700 border-violet-300" : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted"
            )}
          >
            <Checkbox
              checked={on}
              onCheckedChange={() => onToggle(col.key)}
              className={on ? "border-violet-400 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600" : ""}
            />
            {col.label}
          </label>
        );
      })}
    </div>
  </div>
));

// ─── FilterPanel ──────────────────────────────────────────────────────────────

const FilterPanel = memo(({ filters, setFilters, statusOptions, onApply, onClear }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Parent SKU</label>
        <Input placeholder="Parent SKU" value={filters.parent_sku} onChange={e => setFilters(p => ({ ...p, parent_sku: e.target.value }))} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</label>
        <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className={SELECT_CLS}>
          <option value="">All</option>
          {statusOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <Button size="sm" onClick={onApply}>Apply filters</Button>
      <Button size="sm" variant="outline" onClick={onClear}>Clear all</Button>
    </div>
  </div>
));

// ─── PackagingIterationsModal ─────────────────────────────────────────────────

function PackagingIterationsModal({ row, onClose, onPersistIterations, showMessage }) {
  const existingIterations = normalizeIterations(row?.packaging_iterations);
  const hasApproved = existingIterations.some(
    item => String(item?.status || "").trim().toLowerCase() === "approved"
  );
  const [iterations, setIterations] = useState(existingIterations);
  const [draft,      setDraft]      = useState(createIterationDraft(existingIterations.length + 1));
  const [submitting, setSubmitting] = useState(false);

  function validateDraft() {
    if (!draft.date || !draft.link || !draft.status || !draft.status_update_date || !draft.approved_by) {
      showMessage("Please fill all fields.", "warning"); return false;
    }
    const valid = ITERATION_STATUS_OPTIONS.filter(Boolean).map(s => s.toLowerCase());
    if (!valid.includes(draft.status.toLowerCase())) {
      showMessage("Status must be Approved or Rejected.", "warning"); return false;
    }
    return true;
  }

  async function handleSubmit() {
    if (!validateDraft()) return;
    const nextIterations = [...iterations, { ...draft }];
    const approvedLink = getApprovedPackagingLink(nextIterations);
    setSubmitting(true);
    try {
      await onPersistIterations(row.id, nextIterations, approvedLink);
      setIterations(nextIterations);
    } catch (e) {
      showMessage(e.message || "Failed to save iteration.", "error");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);

    if (draft.status.toLowerCase() === "approved") {
      showMessage("Iteration approved. Packaging link updated.");
      onClose();
      return;
    }
    const nextNo = nextIterations.length + 1;
    setDraft(createIterationDraft(nextNo));
    showMessage(`Iteration ${nextNo - 1} rejected. Iteration ${nextNo} opened.`, "warning");
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-full max-w-[780px] max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">Packaging Iterations</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {iterations.length} saved · {hasApproved ? "locked" : "1 draft active"}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>

        {hasApproved && (
          <div className="px-5 py-2 bg-green-50 border-b border-green-200 text-xs text-green-700 font-medium">
            Approved iteration exists — packaging link is locked.
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto bg-slate-50/50">
          <table className="w-full border-collapse" style={{ tableLayout: "fixed", minWidth: 620 }}>
            <colgroup>
              <col style={{ width: 40  }} />
              <col style={{ width: 100 }} />
              <col />
              <col style={{ width: 90  }} />
              <col style={{ width: 104 }} />
              <col style={{ width: 130 }} />
            </colgroup>
            <thead>
              <tr className="bg-white border-b border-border">
                {["#", "Date", "Link", "Status", "Status Date", "Approved By"].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {iterations.map((item, idx) => {
                const isApproved = String(item.status || "").toLowerCase() === "approved";
                return (
                  <tr key={idx} className="border-b border-border bg-white">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{idx + 1}</td>
                    <td className="px-3 py-2 text-xs text-foreground">{item.date || "—"}</td>
                    <td className="px-3 py-2 text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.link
                        ? <a href={item.link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{item.link}</a>
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn(
                        "inline-block px-2 py-0.5 rounded-full text-xs font-medium",
                        isApproved ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      )}>
                        {item.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-foreground">{item.status_update_date || "—"}</td>
                    <td className="px-3 py-2 text-xs text-foreground">{item.approved_by || "—"}</td>
                  </tr>
                );
              })}

              {/* Draft row */}
              {!hasApproved && (
                <tr className="bg-blue-50 border-t-2 border-blue-300">
                  <td className="px-3 py-2 text-xs text-blue-600 font-medium">{draft.iteration_no}</td>
                  <td className="px-2 py-2">
                    <input
                      type="date" value={draft.date}
                      onChange={e => setDraft(p => ({ ...p, date: e.target.value }))}
                      className="w-full text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text" value={draft.link} placeholder="https://…"
                      onChange={e => setDraft(p => ({ ...p, link: e.target.value }))}
                      className="w-full text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={draft.status}
                      onChange={e => setDraft(p => ({ ...p, status: e.target.value }))}
                      className="w-full text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      {ITERATION_STATUS_OPTIONS.map(v => <option key={v} value={v}>{v || "Status"}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="date" value={draft.status_update_date}
                      onChange={e => setDraft(p => ({ ...p, status_update_date: e.target.value }))}
                      className="w-full text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1.5 items-center">
                      <input
                        type="text" value={draft.approved_by} placeholder="Name"
                        onChange={e => setDraft(p => ({ ...p, approved_by: e.target.value }))}
                        className="flex-1 min-w-0 text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <Button size="sm" onClick={handleSubmit} disabled={submitting} className="shrink-0 h-7 text-xs px-2.5">
                        {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}

// ─── ParentSkuModal ───────────────────────────────────────────────────────────

function ParentSkuModal({ popup, onClose }) {
  return (
    <div className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-full max-w-[1100px] max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">SKU Rows for Parent SKU</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{popup.parentSku}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-slate-50/50">
          {popup.loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading SKU rows…
            </div>
          ) : popup.error ? (
            <p className="text-sm text-red-600 py-4">{popup.error}</p>
          ) : popup.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No SKU rows found for this parent SKU.</p>
          ) : (
            <div className="overflow-auto">
              <table className="border-collapse w-full bg-white">
                <thead>
                  <tr>
                    {SKU_POPUP_FIELDS.map(field => (
                      <th key={field} className="sticky top-0 bg-slate-50 border-b border-border text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                        {field}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {popup.rows.map((row, idx) => (
                    <tr key={`${row.id || row.sku || idx}`} className="border-t border-border hover:bg-slate-50">
                      {SKU_POPUP_FIELDS.map(field => {
                        const v = row?.[field];
                        return (
                          <td key={field} className="px-3 py-2 text-xs text-foreground align-top min-w-[120px]">
                            {v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ActionsModal ─────────────────────────────────────────────────────────────

function ActionsModal({ onClose, selectedRows, appliedFilters, onUpdateSelected, onUploadCsv, showMessage }) {
  const [busy, setBusy] = useState({ downloading: false, uploading: false, updating: false });
  const isBusy = Object.values(busy).some(Boolean);
  const set = k => v => setBusy(p => ({ ...p, [k]: v }));

  const dlSelected = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("downloading")(true);
    try {
      downloadCsv(toCsv(selectedRows), `catalogue_selected_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${selectedRows.length} rows.`);
    } finally { set("downloading")(false); }
  };

  const dlAll = async () => {
    set("downloading")(true);
    try {
      const all = []; let pg = 1, hasNext = true;
      while (hasNext) {
        const data = await fetchRows({ page: pg, pageSize: 500, filters: appliedFilters });
        const chunk = parseListResponse(data);
        all.push(...chunk); hasNext = Boolean(data?.next); pg++;
        if (!chunk.length) break;
      }
      downloadCsv(toCsv(all), `catalogue_all_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${all.length} rows.`);
    } catch (e) { showMessage(e.message || "Download failed", "error"); }
    finally { set("downloading")(false); }
  };

  const handleFile = async e => {
    const file = e.target.files?.[0];
    if (!file || isBusy) { e.target.value = ""; return; }
    set("uploading")(true);
    try { await onUploadCsv(await file.text()); }
    catch (err) { showMessage(err.message || "Upload failed", "error"); }
    finally { set("uploading")(false); e.target.value = ""; }
  };

  const updateSel = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("updating")(true);
    try { await onUpdateSelected(); }
    catch (e) { showMessage(e.message || "Update failed", "error"); }
    finally { set("updating")(false); }
  };

  return (
    <div className="fixed z-[5000]" style={{ top: 140, right: "max(20px, calc((100vw - 1600px) / 2 + 20px))" }}>
      <div className="bg-white rounded-2xl border border-border shadow-2xl p-6 w-[420px] max-w-[92vw]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground">Actions</h3>
          {!isBusy && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">Download, update, or import catalogue rows.</p>

        <div className="flex flex-col gap-2.5">
          <div className="flex gap-2">
            <Button className="flex-1 gap-1" onClick={dlSelected} disabled={isBusy}>
              {busy.downloading && <Loader2 className="h-3 w-3 animate-spin" />}
              Download Selected
            </Button>
            <Button variant="outline" className="flex-1 gap-1" onClick={dlAll} disabled={isBusy}>
              {busy.downloading && <Loader2 className="h-3 w-3 animate-spin" />}
              Download All
            </Button>
          </div>

          {selectedRows.length > 0 && (
            <Button onClick={updateSel} disabled={isBusy} className="w-full gap-1">
              {busy.updating && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy.updating ? "Updating…" : `Update Selected (${selectedRows.length})`}
            </Button>
          )}
        </div>

        {isBusy && (
          <div className="mt-4 flex items-center gap-2 text-sm text-primary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {busy.uploading ? "Uploading…" : busy.downloading ? "Downloading…" : "Updating…"}
          </div>
        )}

        <div className="mt-4 p-4 bg-muted/40 rounded-xl border border-dashed border-border">
          <span className="text-sm font-semibold text-foreground block mb-3">Upload CSV</span>
          <label className={cn(
            "flex items-center justify-center text-xs font-semibold cursor-pointer",
            "border border-blue-200 bg-blue-50 text-blue-700 rounded-lg py-2.5 px-3",
            isBusy && "opacity-50 pointer-events-none"
          )}>
            Upload CSV
            <input type="file" accept=".csv,text/csv" disabled={isBusy} onChange={handleFile} className="hidden" />
          </label>
          <p className="text-xs text-muted-foreground mt-2">
            Rows with id are updated; rows without id are created.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── DataTableRow ─────────────────────────────────────────────────────────────

const DataTableRow = memo(function DataTableRow({ row, rowIndex, columns, isSelected, onToggleRow, onCellChange, onOpenIterations, onOpenParentSkuPopup }) {
  return (
    <tr className={cn(
      "border-t border-border transition-colors",
      isSelected ? "bg-violet-50" : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/50",
      !isSelected && "hover:bg-slate-50"
    )}>
      <td className="px-3 py-2 text-center w-11">
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleRow(row.id)} />
      </td>
      {columns.map(col => {
        const raw   = row[col.key];
        const value = raw && typeof raw === "object" ? JSON.stringify(raw) : raw;
        const isDate = col.key.endsWith("_date") || col.key.endsWith("_at");
        const iterCount = col.isIterations ? normalizeIterations(row.packaging_iterations).length : 0;

        return (
          <td key={col.key} className="px-3 py-2 text-sm align-middle overflow-hidden">
            {col.isIterations ? (
              <button
                type="button"
                onClick={() => onOpenIterations(row.id)}
                className="border border-blue-200 bg-blue-50 text-blue-700 rounded-lg px-3 py-1 text-xs font-semibold hover:bg-blue-100 transition-colors"
              >
                Manage ({iterCount})
              </button>
            ) : col.badge ? (
              <StatusBadge value={raw} />
            ) : col.key === "parent_sku" ? (
              <button
                type="button"
                onClick={() => onOpenParentSkuPopup(row.parent_sku)}
                title="Click to view SKU rows for this parent SKU"
                className="text-blue-600 font-bold underline text-sm hover:text-blue-800 whitespace-nowrap"
              >
                {value ?? "—"}
              </button>
            ) : col.editable ? (
              <EditableCell value={value} rowId={row.id} colKey={col.key} onChange={onCellChange} />
            ) : (
              <span className="text-muted-foreground text-xs whitespace-nowrap">
                {isDate ? fmtDate(value) : (value ?? "—")}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}, (prev, next) => (
  prev.row === next.row &&
  prev.rowIndex === next.rowIndex &&
  prev.columns === next.columns &&
  prev.isSelected === next.isSelected &&
  prev.onToggleRow === next.onToggleRow &&
  prev.onCellChange === next.onCellChange &&
  prev.onOpenIterations === next.onOpenIterations &&
  prev.onOpenParentSkuPopup === next.onOpenParentSkuPopup
));

// ─── DataTable ────────────────────────────────────────────────────────────────

const DataTable = memo(function DataTable({ columns, rows, loading, allChecked, selectedIds, onToggleAll, onToggleRow, onCellChange, sort, onSort, colWidths, onResizeStart, onOpenIterations, onOpenParentSkuPopup }) {
  const someSelected = rows.some(r => selectedIds[r.id]);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 340px)" }}>
        <table className="border-collapse w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 44 }} />
            {columns.map(col => <col key={col.key} style={{ width: colWidths[col.key] || col.width || 120 }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-10 border-b border-border">
              <th className="px-3 py-2.5 text-center w-11">
                <Checkbox
                  checked={allChecked ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleAll}
                />
              </th>
              {columns.map(col => {
                const sorted = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => onSort(col.key)}
                    className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer select-none relative"
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      <SortIcon dir={sorted ? sort.dir : null} />
                    </span>
                    <ResizeHandle onMouseDown={e => onResizeStart(e, col.key)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground text-sm">
                  No rows found. Try adjusting your filters.
                </td>
              </tr>
            ) : rows.map((row, ri) => (
              <DataTableRow
                key={row.id}
                row={row}
                rowIndex={ri}
                columns={columns}
                isSelected={!!selectedIds[row.id]}
                onToggleRow={onToggleRow}
                onCellChange={onCellChange}
                onOpenIterations={onOpenIterations}
                onOpenParentSkuPopup={onOpenParentSkuPopup}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ─── CatalougeMaster (main page) ──────────────────────────────────────────────

export default function CatalougeMaster() {
  const [rows,            setRows]            = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [count,           setCount]           = useState(0);
  const [hasNextPage,     setHasNextPage]     = useState(false);
  const [hasPrevPage,     setHasPrevPage]     = useState(false);
  const [page,            setPage]            = useState(1);
  const [pageSize,        setPageSize]        = useState(50);
  const [filters,         setFilters]         = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters,  setAppliedFilters]  = useState({ ...DEFAULT_FILTERS });
  const [selectedIds,     setSelectedIds]     = useState({});
  const [visibleKeys,     setVisibleKeys]     = useState(DEFAULT_VISIBLE);
  const [statusOpts,      setStatusOpts]      = useState(STATUS_FILTER_FALLBACK);
  const [sort,            setSort]            = useState(null);
  const [colWidths,       setColWidths]       = useState({});
  const [panel,           setPanel]           = useState(null);
  const [showActions,     setShowActions]     = useState(false);
  const [iterationsRowId, setIterationsRowId] = useState(null);
  const [parentSkuPopup,  setParentSkuPopup]  = useState({ open: false, loading: false, error: "", parentSku: "", rows: [] });
  const [toast,           setToast]           = useState(null);

  const visibleCols        = useMemo(() => COLUMNS.filter(c => visibleKeys.includes(c.key)), [visibleKeys]);
  const selectedRows       = useMemo(() => rows.filter(r => selectedIds[r.id]), [rows, selectedIds]);
  const allChecked         = useMemo(() => rows.length > 0 && rows.every(r => selectedIds[r.id]), [rows, selectedIds]);
  const totalPages         = Math.max(1, Math.ceil(count / pageSize));
  const activeIterationRow = useMemo(() => rows.find(r => r.id === iterationsRowId) || null, [rows, iterationsRowId]);

  const showMessage = useCallback((msg, type = "success") => setToast({ message: msg, type }), []);

  // Column resize
  const resizeRef = useRef(null);
  const handleResizeStart = useCallback((e, key) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[key] || COLUMNS.find(c => c.key === key)?.width || 120;
    resizeRef.current = { key, startX, startW };
    const onMove = mv => {
      const { key, startX, startW } = resizeRef.current;
      setColWidths(p => ({ ...p, [key]: Math.max(60, startW + mv.clientX - startX) }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  const handleSort = useCallback(key => {
    setSort(prev => {
      if (prev?.key === key) return prev.dir === "asc" ? { key, dir: "desc" } : null;
      return { key, dir: "asc" };
    });
    setPage(1);
  }, []);

  const sortedRows = useMemo(() => {
    if (!sort?.key) return rows;
    return [...rows].sort((a, b) => {
      const av = String(a[sort.key] ?? "").toLowerCase();
      const bv = String(b[sort.key] ?? "").toLowerCase();
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sort]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRows({ page, pageSize, filters: appliedFilters });
      const mapped = parseListResponse(data).map(row => {
        const iterations = normalizeIterations(row.packaging_iterations);
        return { ...row, packaging_iterations: iterations, packaging_link: getApprovedPackagingLink(iterations) || row.packaging_link || "" };
      });
      setRows(mapped);
      setCount(Number(data?.count || 0));
      setHasNextPage(Boolean(data?.next));
      setHasPrevPage(Boolean(data?.previous));
      setSelectedIds({});
    } catch (e) { showMessage(e.message || "Failed to load data", "error"); }
    finally { setLoading(false); }
  }, [appliedFilters, page, pageSize, showMessage]);

  const loadDropdowns = useCallback(async () => {
    try {
      const opts = await fetchDropdownOptions("status");
      setStatusOpts(opts.length ? opts : STATUS_FILTER_FALLBACK);
    } catch { setStatusOpts(STATUS_FILTER_FALLBACK); }
  }, []);

  useEffect(() => { loadData(); },     [loadData]);
  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  const handleCellChange = useCallback((rowId, key, val) => {
    setRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...prev[i], [key]: val };
      return next;
    });
  }, []);

  const openParentSkuPopup = useCallback(async parentSku => {
    const value = String(parentSku || "").trim();
    if (!value) { showMessage("Parent SKU is empty.", "warning"); return; }
    setParentSkuPopup({ open: true, loading: true, error: "", parentSku: value, rows: [] });
    try {
      const skuRows = await fetchSkusByParentSku(value);
      setParentSkuPopup({ open: true, loading: false, error: "", parentSku: value, rows: skuRows });
    } catch (e) {
      setParentSkuPopup({ open: true, loading: false, error: e.message || "Failed to fetch SKU rows", parentSku: value, rows: [] });
    }
  }, [showMessage]);

  const handleUpdateSelected = async () => {
    const editable = COLUMNS.filter(c => c.editable !== false && !c.isIterations).map(c => c.key);
    const payload  = selectedRows.map(row => {
      const obj = { id: row.id };
      editable.forEach(k => {
        let value = row[k];
        if (typeof value === "string") { const t = value.trim(); value = t === "" ? null : t; }
        obj[k] = value ?? null;
      });
      return obj;
    });
    const res = await pseudoBulkUpdate(payload);
    showMessage(`Updated: ${res.updated}, Failed: ${res.failed}`, res.failed ? "warning" : "success");
    await loadData();
  };

  const handleUploadCsv = async text => {
    const matrix = parseCSV(text);
    if (!matrix.length) throw new Error("CSV file is empty");
    const headers = matrix[0].map(h => String(h || "").trim());
    const parsed  = matrix.slice(1)
      .filter(line => line.some(c => String(c || "").trim() !== ""))
      .map(line => {
        const obj = {};
        headers.forEach((h, i) => {
          if (!IMPORTABLE_FIELDS.has(h)) return;
          const raw = String(line[i] ?? "").trim();
          if (raw === "") { obj[h] = null; return; }
          if (h === "id" && /^\d+$/.test(raw)) { obj[h] = Number(raw); return; }
          if (h === "packaging_iterations" && (raw.startsWith("{") || raw.startsWith("["))) {
            try { obj[h] = JSON.parse(raw); return; } catch { obj[h] = raw; return; }
          }
          obj[h] = raw;
        });
        return obj;
      });

    const updates = parsed.filter(r => r.id);
    const creates = parsed.filter(r => !r.id);
    let updated = 0, created = 0, failed = 0;

    if (updates.length) { const res = await pseudoBulkUpdate(updates); updated += res.updated; failed += res.failed; }
    for (const row of creates) {
      try { await createCatalogue(row); created++; }
      catch { failed++; }
    }
    showMessage(`Updated: ${updated}, Created: ${created}, Failed: ${failed}`, failed ? "warning" : "success");
    await loadData();
  };

  const persistIterations = async (rowId, nextIterations, approvedLink) => {
    await updateCatalogue(rowId, { packaging_iterations: nextIterations, packaging_link: approvedLink || null });
    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      const normalized = normalizeIterations(nextIterations);
      return { ...row, packaging_iterations: normalized, packaging_link: getApprovedPackagingLink(normalized) || row.packaging_link || "" };
    }));
  };

  const toggleRow = useCallback(id => setSelectedIds(p => ({ ...p, [id]: !p[id] })), []);
  const toggleAll = useCallback(() => {
    if (allChecked) { setSelectedIds({}); return; }
    const n = {}; rows.forEach(r => { n[r.id] = true; }); setSelectedIds(n);
  }, [allChecked, rows]);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters, search: filters.search.trim() });
    setPage(1);
  }, [filters]);
  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS }); setAppliedFilters({ ...DEFAULT_FILTERS }); setPage(1);
  }, []);

  const togglePanel = k => setPanel(p => p === k ? null : k);
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return start + i;
  });

  return (
    <div className="min-h-screen bg-slate-50/70">
      <Navbar />

      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {activeIterationRow && (
        <PackagingIterationsModal
          row={activeIterationRow}
          onClose={() => setIterationsRowId(null)}
          onPersistIterations={persistIterations}
          showMessage={showMessage}
        />
      )}

      {parentSkuPopup.open && (
        <ParentSkuModal
          popup={parentSkuPopup}
          onClose={() => setParentSkuPopup({ open: false, loading: false, error: "", parentSku: "", rows: [] })}
        />
      )}

      {showActions && (
        <ActionsModal
          onClose={() => setShowActions(false)}
          selectedRows={selectedRows}
          appliedFilters={appliedFilters}
          onUpdateSelected={handleUpdateSelected}
          onUploadCsv={handleUploadCsv}
          showMessage={showMessage}
        />
      )}

      <div className="max-w-[1600px] mx-auto px-5 py-6">

        {/* ── Page header ── */}
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Catalogue Master</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage catalogue links and packaging iterations per SKU</p>
          </div>
          <Button onClick={() => setShowActions(true)}>Actions</Button>
        </div>

        {/* ── Toolbar ── */}
        <div className="bg-white border border-border rounded-xl px-4 py-3 mb-3 flex items-center gap-2.5 flex-wrap">
          <Input
            className="flex-1 min-w-[220px]"
            placeholder="Search parent SKU…"
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
          />
          <Button variant={panel === "filters" ? "default" : "outline"} size="sm" onClick={() => togglePanel("filters")}>
            {`Filters${hasActiveFilters ? " ●" : ""}`}
          </Button>
          <Button variant={panel === "columns" ? "default" : "outline"} size="sm" onClick={() => togglePanel("columns")}>
            Columns
          </Button>
          <Button variant="outline" size="sm" onClick={applyFilters}>Search</Button>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} / page</option>)}
          </select>
        </div>

        {/* ── Panels ── */}
        {panel === "filters" && (
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            statusOptions={statusOpts}
            onApply={applyFilters}
            onClear={clearFilters}
          />
        )}
        {panel === "columns" && (
          <ColumnPicker
            all={COLUMNS}
            visible={visibleKeys}
            onToggle={key => setVisibleKeys(p => {
              if (p.includes(key)) {
                if (p.length === 1) { showMessage("At least one column must be visible.", "warning"); return p; }
                return p.filter(k => k !== key);
              }
              return [...p, key];
            })}
            onSelectAll={() => setVisibleKeys(COLUMNS.map(c => c.key))}
            onReset={() => { setVisibleKeys(DEFAULT_VISIBLE); showMessage("Columns reset."); }}
          />
        )}

        {/* ── Table meta bar ── */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              <strong className="text-foreground">{count.toLocaleString()}</strong> total
            </span>
            {selectedRows.length > 0 && (
              <>
                <span className="text-sm text-primary font-medium">{selectedRows.length} selected</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedIds({})}>
                  Clear selection
                </Button>
              </>
            )}
          </div>
          {sort && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              Sorted by <strong className="text-foreground mx-1">{sort.key}</strong> {sort.dir}
              <button onClick={() => setSort(null)} className="ml-1 text-primary hover:underline text-xs">✕ clear</button>
            </span>
          )}
        </div>

        {/* ── Table ── */}
        <DataTable
          columns={visibleCols}
          rows={sortedRows}
          loading={loading}
          allChecked={allChecked}
          selectedIds={selectedIds}
          onToggleAll={toggleAll}
          onToggleRow={toggleRow}
          onCellChange={handleCellChange}
          sort={sort}
          onSort={handleSort}
          colWidths={colWidths}
          onResizeStart={handleResizeStart}
          onOpenIterations={setIterationsRowId}
          onOpenParentSkuPopup={openParentSkuPopup}
        />

        {/* ── Pagination ── */}
        <div className="flex items-center justify-between mt-4 py-3 flex-wrap gap-3">
          <span className="text-sm text-muted-foreground">
            Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong>
            <span className="ml-2 text-muted-foreground/60">({count.toLocaleString()} rows)</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(1)} disabled={!hasPrevPage}>«</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!hasPrevPage}>‹ Prev</Button>
            {pageNumbers.map(n => (
              <Button key={n} variant={n === page ? "default" : "outline"} size="sm" className="w-8 px-0" onClick={() => setPage(n)}>{n}</Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>Next ›</Button>
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>»</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
